import {gql} from '@apollo/client';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {ValidationErrorLabel} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  getValidationSearchUrl,
  parseStorableValidationErrorRelatedItems,
  StorableValidationErrorRecord,
} from 'inventory/location-validation/ParentStorableValidationErrors';
import notifications from 'notifications';
import {
  StorableSchemaValidationDetailsQuery,
  StorableSchemaValidationDetailsQueryVariables,
} from 'settings/samples/__generated/StorableSchemaValidationDetailsQuery';
import StorableSchemaValidationTable, {
  ErrorInfoWithRelatedItems,
  getItemCountWithAccessibleItemLinks,
  StorableWithValidationInfo,
} from 'settings/samples/StorableSchemaValidationTable';
import * as Headers from 'ui-library/Headers';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import pick from 'util/pick';

export const STORABLE_SCHEMA_VALIDATION_DETAILS_QUERY = gql`
  query StorableSchemaValidationDetailsQuery($storableSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    storableSchema(id: $storableSchemaId) {
      ... on StorableSchemaInterface {
        id
        name
        numValidationFailures
        storablesFailingValidation {
          ... on StorableInterface {
            id
            barcode {
              value
            }
            name
            validationState {
              id
              status
              validationErrorRecords {
                id
                errorLabel
                message
                relatedItems {
                  ... on StorableInterface {
                    id
                    barcode {
                      value
                    }
                    storableSchema {
                      id
                      registry {
                        id
                      }
                    }
                  }
                  ...InaccessibleInfoGraphQL
                }
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export type SchemaWithValidationDetails = ExcludeInaccessible<
  StorableSchemaValidationDetailsQuery['storableSchema']
>;

interface StorableSchemaValidationTabProps {
  storableSchemaId: string;
  registryId: string;
}

interface QueriedProps {
  schemaWithValidationInfo: SchemaWithValidationDetails;
}

function StorableSchemaValidationTab(props: StorableSchemaValidationTabProps & QueriedProps): JSX.Element {
  const findErrorWithRelatedItemsByLabel = (
    errorRecords: Array<StorableValidationErrorRecord>,
    errorLabel: ValidationErrorLabel
  ): ErrorInfoWithRelatedItems => {
    const error = errorRecords.find((error) => error.errorLabel === errorLabel);
    if (error != null) {
      return {
        hasError: true,
        relatedItemBarcodes: error.relatedItemBarcodes,
        totalNumberOfRelatedItems: error.totalNumberOfRelatedItems,
      };
    }
    return {hasError: false, relatedItemBarcodes: [], totalNumberOfRelatedItems: 0};
  };

  // At this point we expect all storables to have barcodes (must be unarchived) & to have a not null validation
  // state. If for some reason that isn't the case, just gracefully filter them out
  // Also hide inaccessible storables, since those can't be shown
  const accessibleStorablesWithErrorInfo: Array<StorableWithValidationInfo> = useMemo(() => {
    const storables = props.schemaWithValidationInfo.storablesFailingValidation.flatMap((storable) =>
      !isInaccessible(storable) && storable.barcode != null && storable.validationState != null
        ? [storable]
        : []
    );

    const storablesWithParsedInfo = storables.map((storable) => ({
      id: storable.id,
      barcode: storable.barcode!.value,
      name: storable.name,
      validationErrorRecords: parseStorableValidationErrorRelatedItems(
        storable.validationState?.validationErrorRecords ?? []
      ),
    }));
    return storablesWithParsedInfo.map((storable) => ({
      ...pick(storable, 'id', 'barcode', 'name'),
      hasCapacityError: storable.validationErrorRecords.some(
        (error) => error.errorLabel === ValidationErrorLabel.OVER_CAPACITY
      ),
      nonLocationsDisallowedError: findErrorWithRelatedItemsByLabel(
        storable.validationErrorRecords,
        ValidationErrorLabel.NON_LOCATION_ITEMS_DISALLOWED
      ),
      itemTypesDisallowedError: findErrorWithRelatedItemsByLabel(
        storable.validationErrorRecords,
        ValidationErrorLabel.ITEM_TYPES_DISALLOWED
      ),
    }));
  }, [props.schemaWithValidationInfo.storablesFailingValidation]);

  return (
    <>
      <div className="u-spacing-top u-spacing-bottom">
        <Headers.Title>Locations with failures</Headers.Title>
        {props.schemaWithValidationInfo.numValidationFailures > 0 ? (
          getItemCountWithAccessibleItemLinks({
            totalItemCount: props.schemaWithValidationInfo.numValidationFailures,
            accessibleItemCount: accessibleStorablesWithErrorInfo.length,
            accessibleItemsHref: getValidationSearchUrl(
              accessibleStorablesWithErrorInfo.map((storable) => storable.barcode),
              props.registryId
            ),
            itemLabel: 'location',
          })
        ) : (
          <span>{props.schemaWithValidationInfo.numValidationFailures}</span>
        )}
      </div>
      <StorableSchemaValidationTable
        storablesWithErrorInfo={accessibleStorablesWithErrorInfo}
        registryId={props.registryId}
      />
    </>
  );
}

export default function QueryingStorableSchemaValidationTab(
  props: StorableSchemaValidationTabProps
): JSX.Element | null {
  const queryResult = useBenchlingQuery<
    StorableSchemaValidationDetailsQuery,
    StorableSchemaValidationDetailsQueryVariables
  >(STORABLE_SCHEMA_VALIDATION_DETAILS_QUERY, {
    variables: {storableSchemaId: props.storableSchemaId},
    renderCachedDataWhenRefetching: true,
    fetchPolicy: 'cache-and-network',
  });

  if (queryResult.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  } else if (queryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load schema validation info; try reloading the page.');
    return null;
  }

  const storableSchema = queryResult.data.storableSchema;
  assert(
    !isInaccessible(storableSchema),
    'Expected storable schema to be accessible before accessing its page'
  );
  return <StorableSchemaValidationTab {...props} schemaWithValidationInfo={storableSchema} />;
}
